


















































































































import Vue from 'vue';
import { required, minLength } from 'vuelidate/lib/validators';
import { IAddress, IAnotherOtherCustomSelectOption } from '@/common/interfaces/shared.interface';
import CustomButton from '@/components/layout/buttons/CustomButton.vue';
import { EInputIdentifier } from '@/common/enums/input-identifier.enum';
import SelectInput from '@/components/myprofile/inputs/SelectInput.vue';
import TextInput from '../inputs/TextInput.vue';
import { ICountry } from '@/common/interfaces/config.interface';

const defaultAddress: IAddress = {
  city: ``,
  countryISO: ``,
  deliveryDefault: false,
  extra: ``,
  receiptDefault: false,
  recipient: ``,
  stateId: ``,
  street: ``,
  streetNumber: ``,
  zipCode: ``,
};

interface IAddressFormComponent {
  EInputIdentifier: typeof EInputIdentifier;
  countryOptions: IAnotherOtherCustomSelectOption[];
  countries: ICountry[];
  internalCopy: IAddress;
  stateOptions: IAnotherOtherCustomSelectOption[];
}

export default Vue.extend({
  components: {
    CustomButton,
    SelectInput,
    TextInput,
  },
  data(): IAddressFormComponent {
    return {
      EInputIdentifier,
      countries: [],
      countryOptions: [],
      internalCopy: { ...defaultAddress },
      stateOptions: [],
    };
  },
  computed: {
    selectedCountry(): ICountry | undefined {
      return this.countries.find(country => country.iso === this.internalCopy.countryISO);
    },
  },
  props: {
    address: {
      default: null,
      type: Object as () => IAddress | null,
    },
    buttonControl: {
      default: () => {},
      type: Object as () => Record<string, string>,
    },
    instantUpdate: {
      default: false,
      type: Boolean,
    },
    isCheckoutForm: {
      default: false,
      type: Boolean,
    },
    readonly: {
      default: false,
      type: Boolean,
    },
    showEmptyOption: {
      default: false,
      type: Boolean,
    },
    showRecipient: {
      default: true,
      type: Boolean,
    },
  },
  async created(): Promise<void> {
    this.countries = await this.$zApiService.getCountries();
    this.countryOptions = this.countries.map(country => ({ text: country.name, value: country.iso }));
    await this.updateStateOptions();
  },
  methods: {
    abort(): void {
      this.resetInternalCopy();
      this.$emit(`abort`);
    },
    onInput(): void {
      if(this.instantUpdate) {
        this.internalCopy.isPhoneNumberRequired = !!this.selectedCountry?.phoneNumberRequired;
        this.$emit(`change`, { ...this.internalCopy });
        this.$emit(`validate`, !this.$v.$anyError);
      }
    },
    resetInternalCopy(): void {
      if(this.address) {
        this.internalCopy = { ...this.address };
      } else {
        this.internalCopy = { ...defaultAddress };
      }
    },
    resetValidations(): void {
      this.$v.$reset();
    },
    save(): void {
      this.$emit(`change`, { ...this.internalCopy });
      this.$emit(`validate`, !this.$v.$anyError);
    },
    async updateStateOptions(): Promise<void> {
      if(this.selectedCountry?.stateRequired) {
        const states = await this.$zApiService.getStates(this.selectedCountry.iso);
        this.stateOptions = states.map(state => ({ text: state.name, value: state.id.toString() }));
      } else {
        this.stateOptions = [];
      }
    },
    validate(): void {
      this.$v.$touch();
      this.$emit(`validate`, !this.$v.$anyError);
    },
  },
  // vuelidate types don't seem to work as advertised, therefore any.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  validations(): any {
    return {
      internalCopy: {
        city: {
          minLength: minLength(1),
          required,
        },
        countryISO: {
          required,
        },
        extra: {},
        recipient: this.showRecipient ? {
          required,
        } : {},
        stateId: this.selectedCountry?.stateRequired ? {
          required,
        } : {},
        street: {
          minLength: minLength(1),
          required,
        },
        streetNumber: {
          required,
        },
        zipCode: {
          required,
        },
      },
    };
  },
  watch: {
    address: {
      deep: true,
      handler(value) {
        if(value != null) {
          this.internalCopy = { ...value };
        } else {
          this.internalCopy = { ...defaultAddress };
        }
      },
      immediate: true,
    },
    'internalCopy.countryISO': {
      deep: true,
      async handler(newVal: string, oldVal: string): Promise<void> {
        if(oldVal === newVal || !this.selectedCountry) {
          return;
        }
        if(!this.selectedCountry.stateRequired) {
          this.internalCopy.stateId = ``;
        }
        await this.updateStateOptions();
      },
      immediate: true,
    },
  },
});
